import React, { ReactElement, useCallback, useMemo } from "react";
import { CountdownCircleProps } from "./CountdownCircle.types";
import { SmallBody, LargeBody } from "../Text";
import { Conditional } from "../Conditional";

const minimumNumberOfDots = 12 as const;

/**
 * CountdownCircle component
 *
 * @param {CountdownCircleProps} props - The component props
 * @returns {ReactElement | null} The rendered component
 */
function CountdownCircle({
    maxValue,
    value,
    radius = 50,
    thickness = 5,
    onClick,
    backgroundColor = "#e0e0e0",
    progressColor = "#3f51b5",
    text,
}: CountdownCircleProps): ReactElement | null {
    const dots = useMemo(() => {
        const center = radius;

        const stepCountCoeff = maxValue >= minimumNumberOfDots
            ? 1
            : Math.ceil(minimumNumberOfDots / maxValue);
        const stepCount = maxValue * stepCountCoeff;

        const angleStep = (2 * Math.PI) / stepCount;
        const dots = [];
        const dotSize = thickness;


        for (let i = 0; i < stepCount; i++) {
            const angle = -Math.PI / 2 + i * angleStep;
            const x = center + (radius - thickness / 2) * Math.cos(angle);
            const y = center + (radius - thickness / 2) * Math.sin(angle);
            const isActive = i < (value * stepCountCoeff);

            dots.push(
                <circle cx={x}
                        cy={y}
                        fill={isActive ? progressColor : backgroundColor}
                        key={i}
                        r={dotSize / 2} />
            );
        }

        return dots;
    }, [maxValue, value, radius, thickness, backgroundColor, progressColor]);

    const handleClick = useCallback<React.MouseEventHandler>(
        (event) => {
            if (!onClick) {
                return;
            }

            event.stopPropagation();
            event.preventDefault();

            onClick();
        }, [onClick]);

    return (
        <div className="CountdownCircle">
            <svg height={radius * 2}
                 width={radius * 2}
                 onClick={handleClick}
                 style={{ cursor: onClick ? 'pointer' : 'default' }} >
                {dots}
            </svg>
            <div className="CountdownCircle-inner" style={{
                width: `${radius * 2}px`,
                height: `${radius * 2}px`,
            }}>
                <LargeBody className="CountdownCircle-value"
                           color="contentDark"
                           weight="medium">
                    {value}
                </LargeBody>
                <Conditional condition={!!text}>
                    <SmallBody className="CountdownCircle-text">
                        {text}
                    </SmallBody>
                </Conditional>
            </div>
        </div>
    );
}

export default CountdownCircle;

